import { isContactMetaKey, type FieldDef } from '@jfh/intake-shared';

/** Compact relative time: "just now", "22m ago", "3h ago", "2d ago". */
export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

/** Absolute, human time used in headers and the activity trail. */
export function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Render any stored submission value as a read-only display string. */
const ADDRESS_KEYS = ['line1', 'line2', 'line3', 'city', 'county', 'postcode', 'country'];

const DAY_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** "Wed 21 Oct, 09:30" (UK time), for sentences. */
export function ukDayTime(iso: string): string {
  // Built from parts: browsers differ on a comma after the weekday.
  const parts = Object.fromEntries(DAY_TIME.formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  return `${parts.weekday} ${parts.day} ${parts.month}, ${parts.hour}:${parts.minute}`;
}

export function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map((v) => displayValue(v)).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    // An address: its lines in order, as one line (not "line1: …, city: …").
    if (ADDRESS_KEYS.some((k) => k in o) && Object.keys(o).every((k) => ADDRESS_KEYS.includes(k) || k === 'sameAsFirst')) {
      return ADDRESS_KEYS.map((k) => o[k])
        .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
        .join(', ');
    }
    try {
      return Object.entries(o)
        .filter(([k]) => !isContactMetaKey(k))
        .map(([k, v]) => `${k}: ${displayValue(v)}`)
        .filter(Boolean)
        .join(', ');
    } catch {
      return JSON.stringify(value);
    }
  }
  return String(value);
}

/** Coerce a stored internal value into an input-friendly string. */
export function asInputValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return displayValue(value);
}

/** Customer fields that actually carry a value (skip layout-only headings). */
export function valueFields(fields: FieldDef[]): FieldDef[] {
  return fields.filter((f) => f.type !== 'heading');
}

/**
 * A repeater value is an array whose items are plain objects (one per entry).
 * Distinguished from a simple multi-value array (strings) so the detail view
 * can render each entry as a labelled sub-block rather than `[object Object]`.
 */
export function isRepeaterValue(value: unknown): value is Record<string, unknown>[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (item) =>
        item !== null &&
        typeof item === 'object' &&
        !Array.isArray(item),
    )
  );
}
